import {useState} from "react";
import {useTableTopOverlayToolbarRef} from "./TableTopOverlayBounds.tsx";
import {CirclePlus, Flame, MapPinPlus, Plus, Redo2, Undo2, Swords} from "lucide-react";
import {Button} from "@/components/ui/button.tsx";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {useAreaEffect} from "@/encounter/effect/AreaEffectHooks.ts";
import CombatActions from "@/tabletop/encounter/CombatActions.tsx";
import NextTurnButton from "@/tabletop/encounter/NextTurnButton.tsx";
import DungeonOptions from "@/tabletop/dungeon/DungeonOptions.tsx";
import {useTableTopHistory} from "@/tabletop/history/TableTopHistoryHooks.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import VisibilityControls from "@/tabletop/visibility/VisibilityControls.tsx";
import {PlayerDisplayTool} from "@/tabletop/player/PlayerDisplayControls.tsx";
import {cn} from "@/lib/utils.ts";
import {useGameMasterMapInteractions} from "@/tabletop/gm/GameMasterMapInteractionHooks.ts";
import CharacterSearchView from "@/encounter/admin/form/CharacterSearchView.tsx";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";

export default function TableTopToolbar({onOpenCombat}: {onOpenCombat?: () => void} = {}) {
    const toolbarRef = useTableTopOverlayToolbarRef();
    const [addOpen, setAddOpen] = useState(false);
    const {addEffect} = useAreaEffect();
    const {addCharacter} = useCombatants();
    const {selectedCombatants} = useTableTopSelection();
    const {canUndo, canRedo, undo, redo} = useTableTopHistory();
    const {placementMode, setPlacementMode, addAttachedLight} =
        useGameMasterMapInteractions();
    const placingAnnotation = placementMode === "annotation";
    const placingLight = placementMode === "light";
    const adding = placementMode !== null;

    const addAreaEffect = (type: "Rect" | "Arc" | "Sphere") => {
        setPlacementMode(null);
        setAddOpen(false);
        void addEffect(type);
    };

    return <nav ref={toolbarRef} aria-label="Tabletop-Werkzeuge"
                className={cn("pointer-events-auto absolute bottom-4 left-1/2 z-30 flex ",
                    " max-w-[calc(100%-1rem)] -translate-x-1/2 items-center gap-1 overflow-x-auto rounded-xl",
                    " border bg-card/95 p-1.5 shadow-xl backdrop-blur-sm")}>
        <PlayerDisplayTool/>
        <span aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border"/>
        <DungeonOptions/>
        <VisibilityControls/>
        <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
                <Button type="button" size="icon" variant={adding ? "secondary" : "ghost"}
                        title="Hinzufügen" aria-label="Hinzufügen" aria-pressed={adding}
                        className="data-[state=open]:bg-secondary">
                    <Plus/>
                </Button>
            </PopoverTrigger>
            <PopoverContent side="top" align="center" className="w-64 space-y-3">
                <h3 className="text-sm font-semibold">Hinzufügen</h3>
                <div>
                    <p className="mb-1 px-2 text-xs text-muted-foreground">Combatant</p>
                    <CharacterSearchView addCharacter={async character => {
                        await addCharacter(character);
                        setAddOpen(false);
                    }}/>
                </div>
                <div className="space-y-1">
                    <Button type="button" size="sm" variant={placingAnnotation ? "secondary" : "ghost"}
                            className="w-full justify-start" aria-pressed={placingAnnotation}
                            onClick={() => {
                                setPlacementMode(placingAnnotation ? null : "annotation");
                                setAddOpen(false);
                            }}>
                        <MapPinPlus/> {placingAnnotation ? "Notiz platzieren beenden" : "Notiz platzieren"}
                    </Button>
                    <Button type="button" size="sm" variant={placingLight ? "secondary" : "ghost"}
                            className="w-full justify-start" aria-pressed={placingLight}
                            onClick={() => {
                                setPlacementMode(placingLight ? null : "light");
                                setAddOpen(false);
                            }}>
                        <Flame/> {placingLight ? "Licht platzieren beenden" : "Licht platzieren"}
                    </Button>
                    {selectedCombatants.length === 1 && <Button type="button" size="sm" variant="ghost"
                                                                className="w-full justify-start" onClick={() => {
                        addAttachedLight(selectedCombatants[0].id);
                        setAddOpen(false);
                    }}>
                        <Flame/> {selectedCombatants[0].name} Licht geben
                    </Button>}
                </div>
                <div className="space-y-1 border-t pt-3">
                    <p className="px-2 text-xs text-muted-foreground">Flächeneffekt</p>
                    <Button type="button" size="sm" variant="ghost" className="w-full justify-start"
                            onClick={() => addAreaEffect("Rect")}><CirclePlus/> Rechteck</Button>
                    <Button type="button" size="sm" variant="ghost" className="w-full justify-start"
                            onClick={() => addAreaEffect("Arc")}><CirclePlus/> Bogen</Button>
                    <Button type="button" size="sm" variant="ghost" className="w-full justify-start"
                            onClick={() => addAreaEffect("Sphere")}><CirclePlus/> Kreis</Button>
                </div>
            </PopoverContent>
        </Popover>
        <span aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border"/>
        <div role="group" aria-label="Kampf" className="flex shrink-0 items-center gap-1">
            <CombatActions/>
            {onOpenCombat && <Button type="button" size="icon" variant="ghost" title="Kampfverwaltung"
                aria-label="Kampfverwaltung" onClick={onOpenCombat}><Swords/></Button>}
            <NextTurnButton onPrepareCombat={onOpenCombat}/>
        </div>
        <span aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border"/>
        <div role="group" aria-label="Rückgängig und Wiederholen" className="flex shrink-0 items-center gap-1">
            <Button type="button" variant="ghost" size="icon" title="Verschieben oder Drehen rückgängig (Strg+Z)"
                    aria-label="Rückgängig" disabled={!canUndo} onClick={undo}><Undo2/></Button>
            <Button type="button" variant="ghost" size="icon" title="Verschieben oder Drehen wiederholen (Strg+Y)"
                    aria-label="Wiederholen" disabled={!canRedo} onClick={redo}><Redo2/></Button>
        </div>
    </nav>;
}
